import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { t } from '../../utils/translations';
import { getProducts, getProductCategories, getProductUnits } from '../../api/product';
import { Product, Category, Unit } from '../../types/product';
import { useNotifications } from '../../hooks/useNotifications';
import ProductEditPanel from '../Products/ProductEditPanel';

interface AddProductPanelProps {
  open: boolean;
  onClose: () => void;
  onAddProduct: (productId: string, quantity: number, comment?: string) => Promise<void>;
  usedProductIds?: string[];
}

const AddProductPanel: React.FC<AddProductPanelProps> = ({ open, onClose, onAddProduct, usedProductIds = [] }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState<string>('1');
  const [comment, setComment] = useState<string>('');
  const [productEditPanelOpen, setProductEditPanelOpen] = useState<boolean>(false);

  const { showError } = useNotifications();

  useEffect(() => {
    if (open) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData, unitsData] = await Promise.all([
        getProducts(),
        getProductCategories(),
        getProductUnits(),
      ]);
      setProducts(productsData.filter((p) => p.status === 1));
      setCategories(categoriesData);
      setUnits(unitsData);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProduct) {
      showError('Wybierz produkt z listy', 'Błąd walidacji');
      return;
    }

    const quantityNum = parseInt(quantity, 10);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      showError('Ilość musi być liczbą dodatnią', 'Błąd walidacji');
      return;
    }

    try {
      setSaving(true);
      await onAddProduct(selectedProduct.id, quantityNum, comment.trim() || undefined);
      handleClose();
    } catch (error) {
      showError(
        error instanceof Error ? error.message : 'Nie udało się dodać produktu',
        'Błąd'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Reset form state when closing
    setSelectedProduct(null);
    setQuantity('1');
    setComment('');
    setProductEditPanelOpen(false);
    onClose();
  };

  const handleProductCreated = async () => {
    // Reload products list
    const productsData = await getProducts();
    const activeProducts = productsData.filter((p) => p.status === 1);
    setProducts(activeProducts);

    // Find the newest product by sorting by ID (assuming IDs are incrementing)
    const sortedByIdDesc = [...activeProducts].sort((a, b) => b.id.localeCompare(a.id));
    const newestProduct = sortedByIdDesc[0];

    // Select the newly created product
    if (newestProduct) {
      setSelectedProduct(newestProduct);
    }

    // Close the product edit panel
    setProductEditPanelOpen(false);
  };

  const handleOpenProductEditPanel = () => {
    setProductEditPanelOpen(true);
  };

  const handleCloseProductEditPanel = () => {
    setProductEditPanelOpen(false);
  };

  return (
    <>
      <Drawer anchor="bottom" open={open} onClose={handleClose}>
        <Box sx={{ padding: 3, minHeight: '400px', maxHeight: '80vh', overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <Typography variant="h6">{t('shoppingLists.addProduct')}</Typography>
            <IconButton onClick={handleClose} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', marginBottom: 2 }}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) => {
                    const isUsed = usedProductIds.includes(option.id);
                    return isUsed ? `${option.name} (użyty)` : option.name;
                  }}
                  getOptionDisabled={(option) => usedProductIds.includes(option.id)}
                  value={selectedProduct}
                  onChange={(_, newValue) => setSelectedProduct(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('products.selectProduct')}
                      required
                      disabled={saving}
                      autoFocus={false}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  sx={{ flex: 1 }}
                />

                <TextField
                  label={
                    selectedProduct
                      ? `${t('shoppingLists.quantity')} (${t(`units.${selectedProduct.unit.toLowerCase()}`)})`
                      : t('shoppingLists.quantity')
                  }
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                  disabled={saving}
                  inputProps={{ min: 1 }}
                  sx={{
                    width: '120px',
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                      '&::-webkit-outer-spin-button': {
                        WebkitAppearance: 'inner-spin-button',
                        opacity: 1,
                      },
                      '&::-webkit-inner-spin-button': {
                        WebkitAppearance: 'inner-spin-button',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>

              <TextField
                label={t('shoppingLists.comment')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                fullWidth
                multiline
                rows={2}
                disabled={saving}
                placeholder={t('shoppingLists.commentPlaceholder')}
                sx={{ marginBottom: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', marginTop: 3 }}>
                <Button onClick={handleClose} disabled={saving}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || !selectedProduct}
                  startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                  {saving ? t('common.saving') : t('common.add')}
                </Button>
              </Box>

              <Divider sx={{ marginY: 3 }} />

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleOpenProductEditPanel}
                  disabled={saving}
                >
                  {t('products.createNewProduct')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Drawer>

      <ProductEditPanel
        open={productEditPanelOpen}
        product={null}
        categories={categories}
        units={units}
        onClose={handleCloseProductEditPanel}
        onUpdate={handleProductCreated}
      />
    </>
  );
};

export default AddProductPanel;